// ----------------------------------------------------------------------------
// Admin app shell: sticky top bar, optional subnav, and the routed page.
// Wraps the same providers the old shared AdminLayout did (church,
// entitlements, groups, restore-organization gate) so every page keeps its
// data sources; only the chrome changed.
// ----------------------------------------------------------------------------

import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@app/auth/AuthContext';
import { ChurchProvider } from '@admin/shared/contexts/ChurchContext';
import { EntitlementsProvider, useEntitlements } from '@admin/shared/contexts/EntitlementsContext';
import { GroupProvider, useGroup } from '@admin/shared/contexts/GroupContext';
import { RestoreChurchGate } from './RestoreChurchGate';
import { featureFlags } from '@admin/shared/featureFlags';
import { LiveNotificationsProvider, useLiveNotifications } from '@app/notifications/LiveNotifications';
import { cx } from '@app/components/ui';
import { Header } from './Header';
import { NewSetlistModal } from './NewSetlistModal';
import { SUBNAV, groupForPath } from './nav';

export function AppShell() {
  return (
    <ChurchProvider>
      <EntitlementsProvider>
        <GroupProvider>
          <RestoreChurchGate>
            <ShellInner />
          </RestoreChurchGate>
        </GroupProvider>
      </EntitlementsProvider>
    </ChurchProvider>
  );
}

function ShellInner() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const { groups, selectedGroupId, setSelectedGroupId } = useGroup();
  const [newSetlistOpen, setNewSetlistOpen] = useState(false);

  // Teams are separate: every scoped page works on one team, so make sure one
  // is chosen once the list loads.
  useEffect(() => {
    if (!selectedGroupId && groups.length) setSelectedGroupId(groups[0].id);
  }, [groups, selectedGroupId, setSelectedGroupId]);

  return (
    <LiveNotificationsProvider churchId={user?.churchId} userId={user?.id}>
      <div className="min-h-screen bg-bg text-t1">
        <Header onNewSetlist={() => setNewSetlistOpen(true)} />
        <main className="max-w-[1280px] mx-auto px-3.5 pt-[22px] pb-24 min-[640px]:px-5 min-[640px]:pt-8">
          <Subnav pathname={pathname} />
          {/* Keyed on the path so each route rises in. */}
          <div key={pathname} className="wp-rise">
            <Outlet />
          </div>
        </main>
        <NewSetlistModal open={newSetlistOpen} onClose={() => setNewSetlistOpen(false)} />
      </div>
    </LiveNotificationsProvider>
  );
}

function Subnav({ pathname }: { pathname: string }) {
  const { canUseChordAi, canUseApiKeys } = useEntitlements();
  const { pendingCount } = useLiveNotifications();
  const items = (SUBNAV[groupForPath(pathname)] ?? []).filter((i) => {
    if (i.gate === 'chordAi') return featureFlags.chordAi && canUseChordAi;
    if (i.gate === 'apiKeys') return canUseApiKeys;
    return true;
  });
  if (items.length < 2) return null;
  return (
    <nav
      aria-label="Section"
      className="flex flex-wrap gap-1 mb-[26px] p-1 rounded-full bg-fill border border-line w-fit max-w-full"
    >
      {items.map((i) => {
        // Link, not NavLink: the current item is decided here (prefix match),
        // and NavLink would drop aria-current when the URL isn't an exact match.
        const on = pathname === i.path || pathname.startsWith(`${i.path}/`);
        const badge = i.badge === 'requests' && pendingCount ? (pendingCount > 9 ? '9+' : String(pendingCount)) : '';
        return (
          <Link
            key={i.path}
            to={i.path}
            aria-current={on ? 'page' : undefined}
            className={cx(
              'h-[34px] px-4 rounded-full text-[13.5px] font-medium flex items-center gap-[7px]',
              on ? 'bg-t1 !text-bg' : 'bg-transparent !text-t3 hover:!text-t1',
            )}
            style={{ transition: 'background .25s ease, color .25s ease' }}
          >
            {i.label}
            {badge && (
              <span className="text-[10.5px] font-bold px-1.5 py-0.5 rounded-full bg-acc text-white" aria-label={`${badge} waiting`}>
                {badge}
              </span>
            )}
          </Link>
        );
      })}
    </nav>
  );
}
